import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { Button, Chip, CircularProgress, Grid, IconButton, LinearProgress, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useLocation } from "react-router-dom";
import { UserService } from "../../../../assets/_services/user-service";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import TextFieldWrapper from "../../../../utils/ui-components/FormsUI/TextField";
import { useDispatch } from "react-redux";
import { MENU_OPEN, SET_BREADCRUMBS } from "../../../../redux/actions/actions";
import { DataGrid, GridAlignment } from "@mui/x-data-grid";
import EmptyResultDataGrid from "../../../../utils/ui-components/EmptyResultDataGrid";
import SelectWrapper from "../../../../utils/ui-components/FormsUI/Select";
import { closeConfirmDialog, openConfirmDialog } from "../../../../utils/ui-components/pop-ups/ConfirmDialog";
import { openSuccessDialog } from "../../../../utils/ui-components/pop-ups/SuccessDialog";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { env } from "process";

function SelfRegUsers(props: { value: number | string }) {
  const theme: any = useTheme();

  const [search, setSearch] = useState({
    username: "",
    status: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [count, setCount] = useState(0);
  const [isLoadingStatus, setIsLoadingStatus] = useState<number | string>("");

  const path = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const id = path?.pathname;
    dispatch({ type: MENU_OPEN, id });
    window.history.replaceState({}, "");
    dispatch({
      type: SET_BREADCRUMBS,
      payload: [
        { name: "Home", path: "/Dashboard", bold: true, state: null },
        { name: "User Management", path: null, bold: false, state: null },
        { name: `Self Reg Users`, path: null, bold: false, state: null },
      ],
    });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchApproverUserList(search);
  }, [paginationModel, search]);

  const fetchApproverUserList = (searchValue: { username: string; status: number | string }) => {
    setIsLoading(true);
    let formData = {
      userName: searchValue.username,
      isViewed: searchValue.status,
    };
    UserService.getSelfRegUsers(formData, paginationModel.page, paginationModel.pageSize).then((response) => {
      if (response.isSuccess) {
        setUsers(response.data.data.rows);
        setCount(response.data.data.count);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });
  };

  const columns = [
    {
      field: "action",
      filterable: false,
      headerName: "",
      sortable: false,
      disableExport: false,
      width: 80,
      headerClassName: "stickyActionHeader",
      cellClassName: "stickyColumn",
      renderCell: (params: any) => {
        return (
          <>
            <Tooltip title="Change Viewed Status">
              <span>
                <IconButton
                  aria-label="view"
                  color="primary"
                  disabled={isLoadingStatus !== "" || Number(search.status) === 1}
                  onClick={() => {
                    openConfirmDialog(
                      "Mark As Viewed",
                      `Are you sure you want to mask as viewed this user?`,
                      {
                        id: params.row.id,
                        companyName: params.row.companyName,
                      },
                      (data) => {
                        setIsLoadingStatus(params.row.id);
                        closeConfirmDialog();
                        let formData = {
                          userId: data.id,
                        };
                        UserService.maskAsViewed(formData).then((response) => {
                          if (response.isSuccess) {
                            openSuccessDialog("Success", `${data.companyName} marked as viewed`);
                            fetchApproverUserList(search);
                            setIsLoadingStatus("");
                          } else {
                            setIsLoadingStatus("");
                          }
                        });
                      }
                    );
                  }}
                >
                  {isLoadingStatus === params.row?.id ? (
                    <CircularProgress size={"20px"} />
                  ) : (
                    <VisibilityIcon color={Number(search.status) === 1 ? "disabled" : "primary"} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Download Attachment">
              <IconButton
                aria-label="view"
                color="primary"
                onClick={async () => {
                  try {
                    const response = await fetch(
                      `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_API_DEV : process.env.REACT_APP_API_PROD}${
                        params.row.documentPath
                      }`
                    );
                    if (!response.ok) {
                      throw new Error("Failed to fetch the image");
                    }
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = params.row.documentPath.split("/").pop();
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  } catch (error) {
                    console.error("Error downloading image:", error);
                  }
                }}
              >
                {isLoadingStatus === params.row?.id ? <CircularProgress size={"20px"} /> : <FileDownloadIcon color="primary" />}
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    {
      field: "id",
      filterable: false,
      headerName: "ID",
      sortable: false,
      disableExport: false,
      maxWidth: 100,
      minWidth: 80,
      flex: 1,
    },
    {
      field: "companyName",
      filterable: false,
      headerName: "Company/Individual Name",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "companyAddress",
      filterable: false,
      headerName: "Company/Individual Address",
      sortable: false,
      disableExport: false,
      minWidth: 220,
      flex: 1,
    },
    {
      field: "primaryContactName",
      filterable: false,
      headerName: "Primary Contact Name",
      sortable: false,
      disableExport: false,
      minWidth: 180,
      flex: 1,
    },
    {
      field: "contact",
      filterable: false,
      headerName: "Contact Number",
      sortable: false,
      disableExport: false,
      minWidth: 150,
      flex: 1,
    },
    {
      field: "email",
      filterable: false,
      headerName: "Email",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "smsSendersName",
      filterable: false,
      headerName: "Sender's Name",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "userName",
      filterable: false,
      headerName: "User Name",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "BrcOrNic",
      filterable: false,
      headerName: "BRC/NIC No",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "vatNumber",
      filterable: false,
      headerName: "VAT No",
      sortable: false,
      disableExport: false,
      minWidth: 150,
      flex: 1,
    },
    {
      field: "status",
      filterable: false,
      headerName: "Is Viewed",
      sortable: false,
      disableExport: false,
      width: 120,
      align: "center" as GridAlignment,
      renderCell: (params: any) => {
        return Number(params?.row?.isViewed) === 1 ? (
          <Chip variant="outlined" color="success" label="Viewed" />
        ) : (
          <Chip variant="outlined" color="error" label="Not Viewed" />
        );
      },
    },
  ];

  return (
    <Grid container justifyContent="center" spacing={2}>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <Formik
          initialValues={{
            username: "",
            status: 0,
          }}
          validationSchema={Yup.object().shape({
            userName: Yup.string()
              .notRequired()
              .matches(/^[a-zA-Z0-9_]*$/, "Username can contain only letters, numbers, and underscores"),
            companyName: Yup.string().notRequired(),
          })}
          onSubmit={(values: any) => {
            setSearch(values);
            setPaginationModel({ ...paginationModel, page: 0 });
            // fetchApproverUserList(values);
          }}
        >
          {() => (
            <Form>
              <Grid container columnSpacing={1} justifyContent={"flex-end"}>
                <Grid item lg={3} md={3} sm={4} xs={4}>
                  <SelectWrapper
                    label="Viewed Status"
                    name="status"
                    options={[
                      { value: 0, label: "Not Viewed" },
                      { value: 1, label: "Viewed" },
                    ]}
                    customHandleChange={() => {}}
                  />
                </Grid>
                <Grid item>
                  <TextFieldWrapper label="Search" placeholder="Username.." name="username" sx={{ minWidth: "250px" }} fullWidth={true} />
                </Grid>
                <Grid item>
                  <Button color="primary" variant="contained" sx={{ ...theme.typography.customInput }} type="submit">
                    View
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <DataGrid
          autoHeight
          columns={columns}
          loading={isLoading}
          rows={users}
          slots={{
            noRowsOverlay: EmptyResultDataGrid,
            loadingOverlay: () => <LinearProgress />,
          }}
          style={{ overflow: "auto" }}
          sx={{
            "& .MuiDataGrid-cell.stickyColumn": {
              position: "sticky",
              left: 0,
              backgroundColor: `${theme.palette.mode === "light" ? theme.palette.primary.light : theme.palette.background.paper}`,
              zIndex: 1,
            },
            "& .clicked-row": {
              backgroundColor: `${theme.palette.mode === "light" ? theme.palette.secondary.light : theme.palette.background.footer} !important`,
            },
            cursor: "pointer",
          }}
          pagination
          getRowId={(row: any) => row.id}
          paginationMode="server"
          rowCount={count}
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
        />
      </Grid>
    </Grid>
  );
}

export default SelfRegUsers;
